// Utility to transform backend expense categories (with optional children) into
// an alphabetically sorted, indented flat list for dropdowns.
// Input format (from API): [
//   { value, label, color?, bg?, children?: [ { value, label, ... } ] }, ...
// ]
// Output: [ { value, label, indentLevel, displayLabel }, ... ]

function buildIndentedCategoryList(raw) {
  try {
    const items = Array.isArray(raw) ? raw : [];
    // Build maps and collect
    const byValue = new Map();
    const parents = [];
    const childValueSet = new Set();

    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      byValue.set(String(it.value), it);
      if (Array.isArray(it.children) && it.children.length > 0) {
        parents.push(it);
        for (const ch of it.children) {
          if (ch && ch.value != null) childValueSet.add(String(ch.value));
        }
      }
    }

    // Standalone top-level items: not a child and not a parent with children
    const standalones = items.filter(it => {
      const v = String(it.value);
      return !childValueSet.has(v) && !(Array.isArray(it.children) && it.children.length > 0);
    });

    // Sort helpers (case-insensitive)
    const byLabelAsc = (a, b) => {
      const la = String(a.label || '').toLowerCase();
      const lb = String(b.label || '').toLowerCase();
      if (la < lb) return -1; if (la > lb) return 1; return 0;
    };

    parents.sort(byLabelAsc);
    standalones.sort(byLabelAsc);

    const out = [];

    // Push parents and their children
    for (const p of parents) {
      out.push({ value: String(p.value), label: String(p.label), indentLevel: 0, displayLabel: String(p.label) });
      const kids = Array.isArray(p.children) ? [...p.children] : [];
      kids.sort(byLabelAsc);
      for (const ch of kids) {
        out.push({ value: String(ch.value), label: String(ch.label), indentLevel: 1, displayLabel: `  └ ${String(ch.label)}` });
      }
    }

    // Append standalone categories
    for (const s of standalones) {
      out.push({ value: String(s.value), label: String(s.label), indentLevel: 0, displayLabel: String(s.label) });
    }

    // Ensure 'none' exists (Uncategorized) at top or bottom? Keep as-is but avoid duplication
    const hasNone = out.some(x => x.value === 'none');
    if (!hasNone) {
      // Insert at top for visibility
      out.unshift({ value: 'none', label: 'Uncategorized', indentLevel: 0, displayLabel: 'Uncategorized' });
    }

    // Final: stable de-dup in case the API had duplicates
    const seen = new Set();
    const deduped = [];
    for (const row of out) {
      if (seen.has(row.value)) continue;
      seen.add(row.value);
      deduped.push(row);
    }
    return deduped;
  } catch (e) {
    // Fallback minimal
    return [ { value: 'none', label: 'Uncategorized', indentLevel: 0, displayLabel: 'Uncategorized' } ];
  }
}

function buildCategoryTreeSorted(raw) {
  try {
    const items = Array.isArray(raw) ? raw : [];
    const childValueSet = new Set();
    // mark children
    for (const it of items) {
      if (it && Array.isArray(it.children)) {
        for (const ch of it.children) {
          if (ch && ch.value != null) childValueSet.add(String(ch.value));
        }
      }
    }
    // top-level are those not appearing as a child
    const topLevel = items.filter(it => it && !childValueSet.has(String(it.value)));
    const byLabelAsc = (a, b) => {
      const la = String(a.label || '').toLowerCase();
      const lb = String(b.label || '').toLowerCase();
      if (la < lb) return -1; if (la > lb) return 1; return 0;
    };
    // Separate none
    let noneItem = null;
    const filteredTop = [];
    for (const it of topLevel) {
      if (String(it.value) === 'none') { noneItem = it; } else { filteredTop.push(it); }
    }
    // sort parents and standalones mixed
    filteredTop.sort(byLabelAsc);
    // sort children for each parent
    const result = filteredTop.map(p => {
      const children = Array.isArray(p.children) ? [...p.children].sort(byLabelAsc) : [];
      const { children: _omit, ...rest } = p;
      return { ...rest, children };
    });
    return { items: result, noneItem: noneItem || { value: 'none', label: 'None' } };
  } catch (e) {
    return { items: [], noneItem: { value: 'none', label: 'None' } };
  }
}

export { buildIndentedCategoryList, buildCategoryTreeSorted };
